/**
 * Supabase Authentication Service
 *
 * Non-React singleton that wraps the Supabase client for magic link authentication.
 * This service can be used by both React components (via useAuth hook) and
 * non-React code (DonorService, export services, etc.).
 *
 * The existing AuthContext (src/core/auth/AuthContext.tsx) provides React-level
 * auth state for the website. This service provides the same capabilities as a
 * plain singleton for use throughout the application layer.
 */

import { supabase, isSupabaseConfigured } from "../supabase/client";
import type {
  Session,
  User,
  AuthChangeEvent,
  EmailOtpType,
} from "@supabase/supabase-js";
import { debugLog, debugWarn, debugError } from "../utils/debug";
import { donorService } from "./donorService";

type AuthStateCallback = (
  event: AuthChangeEvent,
  session: Session | null,
) => void;

class SupabaseAuthService {
  private static instance: SupabaseAuthService;

  private currentSession: Session | null = null;
  private authListeners: Set<AuthStateCallback> = new Set();
  private initialized = false;
  private initializing: Promise<void> | null = null;

  private constructor() {
    // Private constructor for singleton
  }

  public static getInstance(): SupabaseAuthService {
    if (!SupabaseAuthService.instance) {
      SupabaseAuthService.instance = new SupabaseAuthService();
    }
    return SupabaseAuthService.instance;
  }

  /**
   * Initialize the auth service — restores persisted session and sets up
   * the onAuthStateChange listener. Safe to call multiple times (idempotent).
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initializing) return this.initializing;

    this.initializing = this._doInitialize();
    await this.initializing;
  }

  private async _doInitialize(): Promise<void> {
    if (!isSupabaseConfigured() || !supabase) {
      debugWarn(
        "SupabaseAuthService: Supabase not configured. Auth features disabled.",
      );
      this.initialized = true;
      return;
    }

    try {
      // CRITICAL: Register the auth state listener BEFORE getSession().
      // When detectSessionInUrl is true, getSession() exchanges the magic link
      // URL token for a session and fires SIGNED_IN internally. If the listener
      // is registered after getSession(), the event is lost and AuthContext's
      // handleNewUserSignup never runs.
      supabase.auth.onAuthStateChange((event, newSession) => {
        debugLog("SupabaseAuthService: Auth state changed:", event);
        this.currentSession = newSession;

        if (event === "SIGNED_IN" && newSession?.user) {
          debugLog("SupabaseAuthService: User signed in:", newSession.user.id);
          // Fire-and-forget: cache donor status for synchronous checks later
          donorService
            .checkAndCacheDonorStatus(
              newSession.user.id,
              newSession.user.email || "",
            )
            .catch((err) => {
              debugError(
                "SupabaseAuthService: Failed to cache donor status:",
                err,
              );
            });
        } else if (event === "SIGNED_OUT") {
          debugLog("SupabaseAuthService: User signed out");
          donorService.clearCache();
        }

        // Notify all registered listeners
        Array.from(this.authListeners).forEach((callback) => {
          try {
            callback(event, newSession);
          } catch (err) {
            debugError("SupabaseAuthService: Error in auth listener:", err);
          }
        });
      });

      // Restore persisted session (AFTER listener is registered so SIGNED_IN is caught)
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        debugError(
          "SupabaseAuthService: Error getting session:",
          error.message,
        );
      } else {
        this.currentSession = session;
        if (session) {
          debugLog(
            "SupabaseAuthService: Session restored for",
            session.user.email,
          );
        }
      }

      this.initialized = true;
    } catch (err) {
      debugError("SupabaseAuthService: Initialization failed:", err);
      this.initialized = true;
    }
  }

  /**
   * Check if the user is currently authenticated.
   */
  isAuthenticated(): boolean {
    if (!isSupabaseConfigured()) return false;
    return this.currentSession !== null;
  }

  /**
   * Get the current Supabase User object, or null if not authenticated.
   */
  getCurrentUser(): User | null {
    return this.currentSession?.user ?? null;
  }

  /**
   * Get the current user's ID, or null if not authenticated.
   */
  getCurrentUserId(): string | null {
    return this.currentSession?.user?.id ?? null;
  }

  /**
   * Get the current session, or null if not authenticated.
   */
  getSession(): Session | null {
    return this.currentSession;
  }

  /**
   * Send a magic link to the given email address for passwordless login.
   * Optional metadata is written to auth.users.raw_user_meta_data at OTP-send time,
   * so it's available server-side before the user clicks the link.
   */
  async sendMagicLink(
    email: string,
    metadata?: Record<string, string>,
  ): Promise<{ success: boolean; error?: string }> {
    if (!isSupabaseConfigured() || !supabase) {
      return { success: false, error: "Supabase not configured" };
    }

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          // Land on our own confirmation interstitial, NOT straight into the app.
          // A link-scanning mail gateway that pre-fetches the emailed link must not
          // be able to spend the one-time token; only a human pressing the button
          // on /auth/confirm calls verifyOtp. See verifyMagicLink() below and
          // architecture/Website/auth-confirm-s1/falsifying-observation.md.
          //
          // THIS VALUE IS WHAT THE EMAIL TEMPLATES INTERPOLATE. Supabase exposes it
          // as {{ .RedirectTo }}, and TWO live templates are built on it — the CTO
          // seat applies both via the Management API:
          //
          //   magic_link   (returning user)  {{ .RedirectTo }}?token_hash={{ .TokenHash }}&type=magiclink
          //   confirmation (first-time user) {{ .RedirectTo }}?token_hash={{ .TokenHash }}&type=signup
          //
          // BOTH, because signInWithOtp against an unknown address creates the user
          // (shouldCreateUser defaults true) and sends the CONFIRMATION template, not
          // the magic-link one. Fixing only magic_link leaves every first-time user on
          // the token-spending path.
          //
          // ⛔ {{ .RedirectTo }}, NEVER {{ .SiteURL }}. Not a style preference — the
          // reason is PER-ORIGIN: {{ .SiteURL }} is ONE project-wide value and preview
          // and production share one Supabase project, so it would send every sign-in
          // to a single host regardless of where it was requested. (It is also wrong
          // today for a second, incidental reason: site_url ends in /app, so it would
          // mint /app/auth/confirm, which AppRouter matches under ProtectedRoute and
          // bounces to /login. Do not treat that as the reason — fix the site_url and
          // {{ .SiteURL }} would still be wrong.)
          //
          // ⛔ THE VALUE BELOW MUST NEVER GAIN A QUERY STRING OR FRAGMENT. The templates
          // append "?token_hash=..." to it verbatim. A second "?" here — a deep link, a
          // ?next= — swallows token_hash into the preceding parameter and every emailed
          // link stops working. Note this PASSES Supabase's redirect validation (same
          // scheme+host as site_url), so nothing upstream will reject it.
          //
          // Provenance: architecture/Website/auth-template-s1/approval-gate.md.
          emailRedirectTo: `${import.meta.env.VITE_SITE_URL || window.location.origin}/auth/confirm`,
          data: metadata,
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { success: false, error: message };
    }
  }

  /**
   * Exchange a magic-link token hash for a session.
   *
   * This is the ONLY path that spends the one-time token, and it is deliberately
   * NOT called on page load anywhere — AuthConfirmPage calls it from a button's
   * click handler, so a mail gateway or link scanner that pre-fetches the emailed
   * URL cannot consume the token on the user's behalf.
   *
   * Measured before this existed (auth-confirm-s1): the emailed link pointed
   * straight at Supabase's /auth/v1/verify, where a single unauthenticated GET
   * returns 303 having already decided the token's fate.
   */
  async verifyMagicLink(
    tokenHash: string,
    type: EmailOtpType = "magiclink",
  ): Promise<{ success: boolean; error?: string }> {
    if (!isSupabaseConfigured() || !supabase) {
      return { success: false, error: "Supabase not configured" };
    }

    try {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type,
      });

      if (error) {
        debugWarn(
          "SupabaseAuthService: Magic link verification failed:",
          error.message,
        );
        return { success: false, error: error.message };
      }

      debugLog("SupabaseAuthService: Magic link verified");
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      debugError(
        "SupabaseAuthService: Magic link verification threw:",
        message,
      );
      return { success: false, error: message };
    }
  }

  /**
   * Sign out the current user.
   */
  async signOut(): Promise<void> {
    if (!isSupabaseConfigured() || !supabase) return;

    const { error } = await supabase.auth.signOut();
    if (error) {
      debugError("SupabaseAuthService: Error signing out:", error.message);
    }

    this.currentSession = null;
  }

  /**
   * Register a callback for auth state changes.
   * Returns an unsubscribe function.
   */
  onAuthStateChange(callback: AuthStateCallback): () => void {
    this.authListeners.add(callback);
    return () => {
      this.authListeners.delete(callback);
    };
  }
}

export const supabaseAuthService = SupabaseAuthService.getInstance();
